import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoClose, IoArrowForward, IoChevronForward } from "react-icons/io5";
import { getPageConfig } from "@/lib/api";

const NERODUS_IMG = "/nerodus.png";

// Fallback si le backend ne répond pas.
const FALLBACK = {
  name: "Nerodus",
  role: "Bibliothécaire du Dōjō",
  contexts: {
    hub: { greeting: "Bienvenue au Dōjō, créateur.", lines: ["Je suis Nerodus, ton bibliothécaire. Je te guide."], cta_label: "Voir l'accompagnement", cta_to: "/accompagnement" },
    ecole: { greeting: "Bienvenue à l'École.", lines: ["Ici tu trouveras plusieurs formations. Choisis ta voie."], cta_label: "", cta_to: "" },
    formation: { greeting: "Voici le Chemin des 7 Portes.", lines: ["Suis-les dans l'ordre si tu veux, mais tu es libre de naviguer d'une porte à l'autre."], cta_label: "", cta_to: "" },
    lecon: { greeting: "Prends ton temps sur cette étape.", lines: ["La pratique grave le savoir."], cta_label: "", cta_to: "" },
    biblio: { greeting: "Bienvenue à la Bibliothèque.", lines: ["Toutes les ressources du Dōjō, classées par Porte."], cta_label: "", cta_to: "" },
    membres: { greeting: "Voici la communauté du Dōjō.", lines: ["Découvre les autres créateurs."], cta_label: "", cta_to: "" },
    communaute: { greeting: "La communauté t'attend.", lines: ["On se retrouve sur le Discord !"], cta_label: "", cta_to: "" },
    accompagnement: { greeting: "Une séance rien que pour toi.", lines: ["Le Renzoku, c'est 1h en tête-à-tête."], cta_label: "", cta_to: "" },
    carnet: { greeting: "Bienvenue dans ton carnet, jeune conteur.", lines: ["C'est ton Drive personnel : tes exercices se rangent tout seuls par Porte.", "Crée tes propres dossiers et sous-dossiers, avec leur couleur et leur icône.", "Dépose des notes, des fichiers ou des liens Canva / Google Docs — et demande une relecture au Sensei."], cta_label: "", cta_to: "" },
  },
};

function keyFor(pathname) {
  if (pathname.startsWith("/carnet")) return "carnet";
  if (pathname.startsWith("/ecole/formation")) return "formation";
  if (pathname.startsWith("/ecole/porte") || pathname.startsWith("/ecole/lecon")) return "lecon";
  if (pathname.startsWith("/ecole")) return "ecole";
  if (pathname.startsWith("/bibliotheque")) return "biblio";
  if (pathname.startsWith("/membres")) return "membres";
  if (pathname.startsWith("/communaute")) return "communaute";
  if (pathname.startsWith("/accompagnement")) return "accompagnement";
  return "hub";
}

/**
 * Nerodus — le bibliothécaire (chat) du Dōjō. Bulle de guide flottante
 * présente partout. Messages contextuels prédéfinis et ÉDITABLES par
 * l'admin (Paramètres › Espace Créateur › Nerodus).
 */
export default function NerodusGuide() {
  const loc = useLocation();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(FALLBACK);
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState(0);
  const [everOpened, setEverOpened] = useState(false);
  const [hidden, setHidden] = useState(() => typeof window !== "undefined" && localStorage.getItem("nerodus_hidden") === "1");
  const [autoTipsOff, setAutoTipsOff] = useState(() => typeof window !== "undefined" && localStorage.getItem("nerodus_autotips_off") === "1");
  const [tourActive, setTourActive] = useState(() => typeof window !== "undefined" && !!window.__tdTourActive);
  // Nerodus n'apparaît qu'APRÈS l'écran d'intro (sinon il masque le bouton "Entrer").
  const [ready, setReady] = useState(() => {
    if (typeof window === "undefined") return true;
    const introWillPlay = window.location.pathname === "/hub"
      && sessionStorage.getItem("sx_intro_shown") !== "1"
      && localStorage.getItem("sx_intro_off") !== "1";
    return !introWillPlay;
  });

  useEffect(() => {
    if (ready) return undefined;
    const done = () => setReady(true);
    window.addEventListener("td-intro-done", done);
    const iv = setInterval(() => { if (sessionStorage.getItem("sx_intro_shown") === "1") setReady(true); }, 400);
    return () => { window.removeEventListener("td-intro-done", done); clearInterval(iv); };
  }, [ready]);

  useEffect(() => { getPageConfig("nerodus").then((d) => setCfg({ ...FALLBACK, ...d, contexts: { ...FALLBACK.contexts, ...(d.contexts || {}) } })).catch(() => {}); }, []);

  // Préférences utilisateur (Paramètres › Confort) : masquer / conseils auto.
  useEffect(() => {
    const onPrefs = (e) => {
      setHidden(localStorage.getItem("nerodus_hidden") === "1");
      setAutoTipsOff(localStorage.getItem("nerodus_autotips_off") === "1");
      if (e?.detail?.replay) { setOpen(true); setEverOpened(true); }
    };
    window.addEventListener("nerodus-prefs", onPrefs);
    return () => window.removeEventListener("nerodus-prefs", onPrefs);
  }, []);

  // Masquer Nerodus pendant la visite guidée.
  useEffect(() => {
    const onTour = (e) => setTourActive(!!e?.detail);
    window.addEventListener("td-tour-active", onTour);
    return () => window.removeEventListener("td-tour-active", onTour);
  }, []);

  const key = keyFor(loc.pathname);
  const ctx = useMemo(() => cfg.contexts[key] || FALLBACK.contexts[key] || FALLBACK.contexts.hub, [cfg, key]);
  const lines = (ctx.lines && ctx.lines.length ? ctx.lines : [""]);

  // Auto-ouverture UNE SEULE FOIS par contexte (mémorisé durablement), sauf si conseils auto désactivés.
  useEffect(() => {
    if (!ready || autoTipsOff) return undefined;
    setTip(0);
    const seenKey = `nerodus_seen_${key}`;
    if (localStorage.getItem(seenKey) === "1") return undefined;
    const t = setTimeout(() => { setOpen(true); setEverOpened(true); localStorage.setItem(seenKey, "1"); }, 900);
    return () => clearTimeout(t);
  }, [key, ready, autoTipsOff]);

  const toggle = () => { setOpen((o) => !o); setEverOpened(true); };
  const nextTip = () => setTip((t) => (t + 1) % lines.length);
  const hasCta = ctx.cta_label && ctx.cta_to;
  const img = cfg.image || NERODUS_IMG;

  if (!ready || hidden || tourActive) return null;

  return (
    <div style={styles.wrap} data-testid="nerodus-guide">
      {open && (
        <div style={styles.panel} data-testid="nerodus-panel">
          <button style={styles.close} onClick={() => setOpen(false)} data-testid="nerodus-close" aria-label="Fermer"><IoClose size={16} /></button>
          <div style={styles.head}>
            <img src={img} alt="Nerodus" style={styles.headImg} />
            <div>
              <div style={styles.name}>{cfg.name || "Nerodus"}</div>
              <div style={styles.role}>{cfg.role || "Bibliothécaire du Dōjō"}</div>
            </div>
          </div>
          <div style={styles.body}>
            <p style={styles.greeting}>{ctx.greeting}</p>
            <p style={styles.line} data-testid="nerodus-tip">{lines[tip]}</p>
          </div>
          <div style={styles.actions}>
            {lines.length > 1 && (
              <button style={styles.tipBtn} onClick={nextTip} data-testid="nerodus-next-tip">
                Conseil suivant <IoChevronForward size={13} />
              </button>
            )}
            {hasCta && (
              <button style={styles.ctaBtn} onClick={() => { setOpen(false); nav(ctx.cta_to); }} data-testid="nerodus-cta">
                {ctx.cta_label} <IoArrowForward size={14} />
              </button>
            )}
          </div>
          {!autoTipsOff && (
            <button style={styles.mute} data-testid="nerodus-stop-autotips"
              onClick={() => { localStorage.setItem("nerodus_autotips_off", "1"); setAutoTipsOff(true); window.dispatchEvent(new CustomEvent("nerodus-prefs")); }}>
              Ne plus afficher automatiquement
            </button>
          )}
          {lines.length > 1 && (
            <div style={styles.dots}>
              {lines.map((_, i) => <span key={i} style={{ ...styles.dot, ...(i === tip ? styles.dotOn : {}) }} />)}
            </div>
          )}
        </div>
      )}

      <button style={styles.bubble} onClick={toggle} data-testid="nerodus-bubble" aria-label="Nerodus, ton guide" title="Nerodus, ton guide">
        {!open && !everOpened && <span style={styles.pulse} />}
        <img src={img} alt="Nerodus" style={styles.bubbleImg} />
        {!open && <span style={styles.badge}>?</span>}
      </button>
    </div>
  );
}

const styles = {
  wrap: { position: "fixed", right: 16, bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)", zIndex: 9500, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12, pointerEvents: "none" },
  bubble: { pointerEvents: "auto", position: "relative", width: 60, height: 60, borderRadius: "50%", border: "2px solid var(--brand)", background: "var(--bg-secondary)", cursor: "pointer", padding: 0, overflow: "visible", boxShadow: "0 10px 28px rgba(0,0,0,0.35)", flexShrink: 0 },
  bubbleImg: { width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover", objectPosition: "top center" },
  pulse: { position: "absolute", inset: -4, borderRadius: "50%", border: "2px solid var(--brand)", animation: "nerodusPulse 1.8s ease-out infinite" },
  badge: { position: "absolute", top: -4, right: -4, width: 22, height: 22, borderRadius: "50%", background: "var(--brand)", color: "#fff", fontSize: 13, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid var(--bg-primary)" },
  panel: { pointerEvents: "auto", position: "relative", width: "min(320px, calc(100vw - 32px))", borderRadius: 20, background: "var(--bg-secondary)", border: "1px solid var(--border-c)", boxShadow: "0 24px 60px rgba(0,0,0,0.4)", padding: 18, animation: "nerodusIn .28s cubic-bezier(.16,1,.3,1)", transformOrigin: "bottom right" },
  close: { position: "absolute", top: 10, right: 10, width: 28, height: 28, borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  headImg: { width: 46, height: 46, borderRadius: "50%", objectFit: "cover", objectPosition: "top center", border: "2px solid var(--brand)" },
  name: { fontWeight: 900, fontSize: 16, color: "var(--text-primary)", fontFamily: "var(--font-display)", letterSpacing: 0.5 },
  role: { fontSize: 11.5, color: "var(--brand)", fontWeight: 700 },
  body: { marginBottom: 14 },
  greeting: { fontSize: 14.5, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 6px" },
  line: { fontSize: 13.5, lineHeight: 1.55, color: "var(--text-secondary)", margin: 0 },
  actions: { display: "flex", flexWrap: "wrap", gap: 8 },
  tipBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "8px 12px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  ctaBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 999, border: "none", background: "var(--brand)", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  dots: { display: "flex", gap: 5, marginTop: 12, justifyContent: "center" },
  mute: { display: "block", margin: "10px auto 0", padding: 0, border: "none", background: "transparent", color: "var(--text-secondary)", fontSize: 11.5, textDecoration: "underline", cursor: "pointer" },
  dot: { width: 6, height: 6, borderRadius: "50%", background: "var(--border-c)" },
  dotOn: { background: "var(--brand)", width: 16, borderRadius: 999 },
};

if (typeof document !== "undefined" && !document.getElementById("nerodus-kf")) {
  const el = document.createElement("style");
  el.id = "nerodus-kf";
  el.textContent = "@keyframes nerodusPulse{0%{transform:scale(1);opacity:.7}100%{transform:scale(1.5);opacity:0}}@keyframes nerodusIn{from{opacity:0;transform:translateY(12px) scale(.94)}to{opacity:1;transform:translateY(0) scale(1)}}";
  document.head.appendChild(el);
}
